import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Lock, Unlock, Settings2, Check, AlertCircle } from "lucide-react";

/**
 * Admin site-wide feature flags.
 * Currently exposes: wiki_restricted (public wiki visibility toggle).
 */
export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const r = await client.get("/settings/site");
      setSettings(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load settings");
    }
  };
  useEffect(() => { load(); }, []);

  const toggleWiki = async () => {
    if (!settings) return;
    const next = !settings.wiki_restricted;
    const confirmMsg = next
      ? "Restrict the /wiki section to admins? Public visitors will see a 'Coming Soon' page instead."
      : "Re-open the /wiki section to the public? Everyone will regain access.";
    if (!window.confirm(confirmMsg)) return;
    setSaving(true);
    setError(null);
    try {
      const r = await client.put("/admin/settings/site", { wiki_restricted: next });
      setSettings(r.data);
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[var(--t-blue-glow)]" /> Site Settings
          </h2>
          <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1 max-w-2xl">
            Feature flags applied site-wide. Changes are live for all visitors instantly — no deploy needed.
          </p>
        </div>
        {savedAt && (
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-[#5fd99a] uppercase">
            <Check className="w-3 h-3" /> saved · {savedAt.toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && (
        <div className="panel border-[var(--t-red)] p-3 mb-4 flex items-start gap-2 text-sm" data-testid="settings-error">
          <AlertCircle className="w-4 h-4 text-[var(--t-red)] flex-shrink-0 mt-0.5" />
          <span className="text-[var(--t-red)] font-mono text-xs">{error}</span>
        </div>
      )}

      {settings === null ? (
        <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow py-8 text-center">
          // loading settings...
        </div>
      ) : (
        <div className="space-y-3" data-testid="settings-list">
          {/* Wiki restriction toggle */}
          <div className="panel corner-frame p-4 md:p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div
                className="w-10 h-10 flex-shrink-0 flex items-center justify-center border"
                style={{
                  borderColor: settings.wiki_restricted ? "var(--t-gold)" : "#5fd99a",
                  background: settings.wiki_restricted ? "rgba(243,197,82,0.08)" : "rgba(95,217,154,0.08)",
                }}
              >
                {settings.wiki_restricted ? (
                  <Lock className="w-4 h-4 text-[var(--t-gold)]" />
                ) : (
                  <Unlock className="w-4 h-4" style={{ color: "#5fd99a" }} />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display text-base text-[var(--t-text)] uppercase tracking-[0.05em]">
                  Restrict Wiki to admins
                </div>
                <p className="text-xs text-[var(--t-text-dim)] mt-1 max-w-xl leading-relaxed">
                  When enabled, the entire <code className="font-mono text-[var(--t-cyan)]">/wiki</code> section (Codex,
                  Items, Badges, Skills, World, Missions, Stats, and every article) shows a &quot;Coming Soon&quot; page to
                  the public. Admins keep full access for editing.
                </p>
                <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: settings.wiki_restricted ? "var(--t-gold)" : "#5fd99a" }}>
                  · currently {settings.wiki_restricted ? "RESTRICTED" : "PUBLIC"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleWiki}
              disabled={saving}
              role="switch"
              aria-checked={settings.wiki_restricted}
              className={`relative inline-flex h-8 w-16 flex-shrink-0 items-center border transition ${
                settings.wiki_restricted
                  ? "border-[var(--t-gold)] bg-[rgba(243,197,82,0.15)]"
                  : "border-[var(--t-border-glow)] bg-[rgba(58,160,255,0.08)]"
              } ${saving ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              data-testid="toggle-wiki-restricted"
              title="Toggle wiki restriction"
            >
              <span
                className="inline-block h-6 w-6 transition-transform"
                style={{
                  transform: settings.wiki_restricted ? "translateX(34px)" : "translateX(4px)",
                  background: settings.wiki_restricted ? "var(--t-gold)" : "var(--t-cyan)",
                  boxShadow: `0 0 12px ${settings.wiki_restricted ? "rgba(243,197,82,0.7)" : "rgba(102,230,255,0.55)"}`,
                }}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
